import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Box } from "@mui/material";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { AuthProvider } from "./context/EnterpriseAuthContext";
import { AppProvider, useAppActions } from "./context/AppContext";
import { SettingsProvider } from "./context/SettingsContext";
import { UsageProvider } from "./context/UsageContext";
import { AIJobCompletionWatcher, AIRequestProvider } from "./context/AIRequestContext";
import { ChatMemoryProvider } from "./context/ChatMemoryContext";
import { ActiveEditorProvider } from "./context/ActiveEditorContext";
import { WorkspaceLayoutProvider, useWorkspaceLayout } from "./context/WorkspaceLayoutContext";
import Navbar from "./components/Navbar";
import SendFeedback from "./components/SendFeedback";
import Login from "./components/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminPanel from "./components/AdminPanel";
import ProjectList from "./components/ProjectList";
import ProjectDetail from "./components/ProjectDetail";
import TemplateList from "./components/TemplateList";
import TemplateEditor from "./components/TemplateEditor";
import PowerPointRichTemplateEditor from "./components/PowerPointRichTemplateEditor";
import DocumentGenerator from "./components/DocumentGenerator";
import DocumentList from "./components/DocumentList";
import CustomerList from "./components/CustomerList";
import CustomerDashboard from "./components/CustomerDashboard";
import AllSessionsPage from "./components/AllSessionsPage";
import SessionHubPage from "./components/SessionHubPage";
import ResetPassword from "./components/ResetPassword";
import AccountInfo from "./components/AccountInfo";
import Profile from "./components/Profile";
import StandaloneEditor from "./components/StandaloneEditor";
import GoGeneratePage from "./components/GoGeneratePage";
import { useAuth } from "./context/EnterpriseAuthContext";
import { applyDocumentThemeMode, darkTheme, lightTheme, readStoredThemeMode, THEME_STORAGE_KEY } from "./theme";
import CompleteProfile from "./components/CompleteProfile";
import EmailConfirmation from "./components/EmailConfirmation";
import Home from "./components/Home";
import StripeBillingReturnRedirect from "./components/StripeBillingReturnRedirect";
import LandingPage from "./components/LandingPage";
import UseCasesPage from "./components/UseCasesPage";
import PricingPage from "./components/PricingPage";
import AboutPage from "./components/AboutPage";
import CareersPage from "./components/CareersPage";
import BlogComingSoonPage from "./components/BlogComingSoonPage";
import DevsComingSoonPage from "./components/DevsComingSoonPage";
import WhatsNewPage, { WHATS_NEW_PATH } from "./components/WhatsNewPage";
import UnsubscribePage, { scrubUnsubscribeToken, UNSUBSCRIBE_PATH } from "./components/UnsubscribePage";
import RoleBasedProtectedRoute from "./components/RoleBasedProtectedRoute";
import NotFound from "./components/NotFound";
import { ConsolePage, CONSOLE_PATH } from "./components/console";
import WorkspaceShell from "./components/layout/WorkspaceShell";
import AppSidebar from "./components/layout/AppSidebar";
import InkyDockContainer from "./components/layout/InkyDockContainer";
import CommandPalette from "./components/layout/CommandPalette";
import ShortcutsHelpDialog from "./components/layout/ShortcutsHelpDialog";
import SettingsHost from "./components/layout/SettingsHost";
import QuickCreateHost from "./components/layout/QuickCreateHost";
import PendingInvitesPrompt from "./components/layout/PendingInvitesPrompt";
import useWorkspaceShortcuts from "./hooks/useWorkspaceShortcuts";
import { legacyEditorRedirectTarget, safeReturnPath, setRecentDocumentsScope } from "./utils/navigation";

/**
 * Per-route browser-tab titles so tabs, history, and screen readers can tell pages apart
 * (QA-018). Ordered longest-prefix-first where routes nest.
 */
const ROUTE_TITLES = [
  ["/home", "Home"],
  ["/templates", "Templates"],
  ["/customers/sessions", "Recordings"],
  ["/customers", "Customers"],
  ["/projects", "Projects"],
  ["/generate", "Generate"],
  ["/go/generate", "Creating your document"],
  ["/documents", "Documents"],
  ["/admin", "Admin"],
  [CONSOLE_PATH, "Console"],
  ["/account-info", "Account"],
  ["/profile", "Profile"],
  ["/login", "Sign In"],
  ["/reset-password", "Reset Password"],
  ["/complete-profile", "Complete Profile"],
  ["/use-cases", "Use Cases"],
  ["/pricing", "Pricing"],
  ["/about", "About"],
  ["/careers", "Careers"],
  ["/blog", "Blog"],
  [WHATS_NEW_PATH, "What's new"],
  [UNSUBSCRIBE_PATH, "Unsubscribe"],
];

/**
 * Routes rendered without the app shell (marketing site, auth flows). They stay public for
 * signed-in users too: the marketing header swaps its sign-in buttons for "Open app".
 */
const MARKETING_PATHS = ["/", "/use-cases", "/pricing", "/about", "/careers", "/blog", WHATS_NEW_PATH];
/** Email-link pages that must work for anyone, signed in or not (no shell, no profile gate). */
const EMAIL_LINK_PATHS = [UNSUBSCRIBE_PATH];
const CHROMELESS_PATHS = [
  "/login",
  "/auth/confirm",
  "/complete-profile",
  "/reset-password",
  ...MARKETING_PATHS,
  ...EMAIL_LINK_PATHS,
];

/**
 * Old `/editor?documentId=...` links (bookmarks, other tabs, Inky's history) redirect to the
 * in-app document workspace, keeping their other query params.
 * @returns {JSX.Element} Redirect.
 */
function LegacyEditorRedirect() {
  const { search } = useLocation();
  return <Navigate to={legacyEditorRedirectTarget(search)} replace />;
}

/**
 * Document workspace keyed by document id, so moving from one document to another starts a
 * fresh editor session (load state, autosave baseline, undo history) instead of reusing one.
 * @returns {JSX.Element} Editor.
 */
function DocumentWorkspaceRoute() {
  const { documentId } = useParams();
  return <StandaloneEditor key={documentId} />;
}

/**
 * All routes. Memoized with no props so shell, Inky and AI-job updates never re-render the
 * routed page; it re-renders only for navigation (and its own context subscriptions).
 * @returns {JSX.Element} Routes.
 */
const AppRoutes = memo(function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/use-cases" element={<UseCasesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/blog" element={<BlogComingSoonPage />} />
      <Route path={WHATS_NEW_PATH} element={<WhatsNewPage />} />
      <Route path="/devs/:slug" element={<DevsComingSoonPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/confirm" element={<EmailConfirmation />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/complete-profile" element={<CompleteProfile />} />
      {/* Unsubscribe link from marketing emails: public on purpose, never behind ProtectedRoute. */}
      <Route path={UNSUBSCRIBE_PATH} element={<UnsubscribePage />} />

      {/* Protected Routes */}
      <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><StripeBillingReturnRedirect /></ProtectedRoute>} />
      <Route path="/settings/" element={<ProtectedRoute><StripeBillingReturnRedirect /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><CustomerList /></ProtectedRoute>} />
      <Route path="/customers/sessions" element={<ProtectedRoute><AllSessionsPage /></ProtectedRoute>} />
      <Route path="/customers/sessions/:sessionId" element={<ProtectedRoute><SessionHubPage /></ProtectedRoute>} />
      <Route path="/customers/:customerId" element={<ProtectedRoute><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectList /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplateList /></ProtectedRoute>} />
      <Route
        path="/templates/new"
        element={<RoleBasedProtectedRoute requiredRole="template_creator"><TemplateEditor /></RoleBasedProtectedRoute>}
      />
      <Route
        path="/templates/:templateId/edit"
        element={<RoleBasedProtectedRoute requiredRole="template_creator"><TemplateEditor /></RoleBasedProtectedRoute>}
      />
      {/* Legacy non-rich PowerPoint editor routes removed from navigation */}
      <Route
        path="/templates/powerpoint-rich/new"
        element={<RoleBasedProtectedRoute requiredRole="template_creator"><PowerPointRichTemplateEditor /></RoleBasedProtectedRoute>}
      />
      <Route
        path="/templates/powerpoint-rich/:templateId/edit"
        element={<RoleBasedProtectedRoute requiredRole="template_creator"><PowerPointRichTemplateEditor /></RoleBasedProtectedRoute>}
      />
      <Route path="/generate" element={<ProtectedRoute><DocumentGenerator /></ProtectedRoute>} />
      {/* One-click "Create <template>" from the meeting recap email and Ready for you (ids only in the link). */}
      <Route path="/go/generate" element={<ProtectedRoute><GoGeneratePage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentList /></ProtectedRoute>} />
      {/* In-app document workspace (editor beside Inky); replaces the old new-tab /editor flow. */}
      <Route path="/documents/:documentId/edit" element={<ProtectedRoute><DocumentWorkspaceRoute /></ProtectedRoute>} />
      <Route path="/editor" element={<LegacyEditorRedirect />} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      {/* Platform owner console: the platform-admin function decides access and 404s everyone else. */}
      <Route path={CONSOLE_PATH} element={<ProtectedRoute><ConsolePage /></ProtectedRoute>} />
      <Route path="/account-info" element={<ProtectedRoute><AccountInfo /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      {/* Catch-all Route for 404 Not Found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
});

// Theme state lives above the router so AppShell (a route element) can read it
// without the router being recreated on toggle.
const ThemeModeContext = React.createContext({ isDarkMode: false, toggleTheme: () => {} });

/**
 * Reload workspace data when an AI job finishes while the app is open: projects for documents,
 * requirements and insights; templates for template imports. Mounted beside the router, so it
 * keeps working in focus mode (no top bar) and on every page.
 * @returns {JSX.Element} Watcher.
 */
function AIJobCompletionReloader() {
  const appActions = useAppActions();
  const onJobsCompleted = useCallback(
    (rows) => {
      if (rows.some((row) => row.type !== "template_import")) appActions.loadProjects();
      if (rows.some((row) => row.type === "template_import")) appActions.loadTemplates();
      // Meeting Autopilot can create customers when it files a meeting.
      if (rows.some((row) => row.type === "meeting_autopilot")) appActions.loadCustomers?.();
    },
    [appActions]
  );
  return <AIJobCompletionWatcher onJobsCompleted={onJobsCompleted} />;
}

/**
 * Global overlays that belong to the signed-in workspace: command palette, shortcuts help,
 * settings, quick-create, pending workspace invitations and feedback dialogs, plus the global
 * keyboard shortcuts.
 * @param {object} props - Props.
 * @param {boolean} props.isDarkMode - Theme.
 * @param {() => void} props.toggleTheme - Theme toggle.
 * @param {boolean} props.feedbackOpen - Feedback dialog state.
 * @param {(open: boolean) => void} props.setFeedbackOpen - Feedback dialog setter.
 * @returns {JSX.Element} Overlays.
 */
function WorkspaceOverlays({ isDarkMode, toggleTheme, feedbackOpen, setFeedbackOpen }) {
  useWorkspaceShortcuts({ enabled: true });
  const openFeedback = useCallback(() => setFeedbackOpen(true), [setFeedbackOpen]);
  return (
    <>
      <CommandPalette isDarkMode={isDarkMode} toggleTheme={toggleTheme} onOpenFeedback={openFeedback} />
      <ShortcutsHelpDialog />
      <SettingsHost />
      <QuickCreateHost />
      <PendingInvitesPrompt />
      <SendFeedback open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
}

/**
 * Route guards plus layout: the signed-in workspace (sidebar, top bar, Inky dock) or the bare
 * marketing/auth pages.
 * @returns {JSX.Element} App content.
 */
function AppContent() {
  const location = useLocation();
  const { state: authState } = useAuth();
  const { isDarkMode, toggleTheme } = React.useContext(ThemeModeContext);
  const { setMobileNavOpen } = useWorkspaceLayout();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const signedInUserId = authState.isAuthenticated ? authState.user?.id || null : null;
  const activeAccountId = authState.activeAccount?.id || null;

  // Recent documents belong to one user in one workspace (none while signed out). Layout
  // effect so the scope is in place before the palette or editor reads or records.
  useLayoutEffect(() => {
    setRecentDocumentsScope({ userId: signedInUserId, accountId: activeAccountId });
  }, [signedInUserId, activeAccountId]);

  useEffect(() => {
    const match = ROUTE_TITLES.find(
      ([prefix]) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
    );
    document.title = match ? `${match[1]} - Quill AI` : "Quill AI";
  }, [location.pathname]);

  // "/whats-new/" and "/whats-new" are the same page; only the root keeps its slash.
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
  const showShell =
    authState.isAuthenticated &&
    !authState.requiresPasswordReset &&
    !CHROMELESS_PATHS.includes(normalizedPath) &&
    !location.pathname.startsWith("/devs/");

  /**
   * Marketing/auth pages scroll the window; reset it on navigation (the shell resets its own
   * scroller). Skips when the home path carries a section hash so /#features still jumps.
   */
  useLayoutEffect(() => {
    if (showShell) return;
    if (location.pathname === "/" && location.hash.length > 1) return;
    window.scrollTo(0, 0);
  }, [showShell, location.pathname, location.hash]);

  const openFeedback = useCallback(() => setFeedbackOpen(true), []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), [setMobileNavOpen]);
  const renderSidebar = useCallback(
    ({ collapsed, inDrawer }) => (
      <AppSidebar
        collapsed={collapsed}
        inDrawer={inDrawer}
        isDarkMode={isDarkMode}
        toggleTheme={toggleTheme}
        onOpenFeedback={openFeedback}
        onNavigate={inDrawer ? closeMobileNav : undefined}
      />
    ),
    [isDarkMode, toggleTheme, openFeedback, closeMobileNav]
  );
  const topbar = useMemo(() => <Navbar onOpenFeedback={openFeedback} />, [openFeedback]);
  const dock = useMemo(() => <InkyDockContainer />, []);

  if (authState.requiresPasswordReset && location.pathname !== "/reset-password") {
    return <Navigate to="/reset-password" replace />;
  }

  if (
    authState.isAuthenticated &&
    authState.needsProfileCompletion &&
    location.pathname !== "/complete-profile" &&
    !EMAIL_LINK_PATHS.includes(normalizedPath)
  ) {
    return <Navigate to="/complete-profile" replace />;
  }

  if (
    authState.isAuthenticated &&
    !authState.needsProfileCompletion &&
    !authState.requiresPasswordReset &&
    location.pathname === "/login"
  ) {
    return <Navigate to={safeReturnPath(location.state?.from) || "/home"} replace />;
  }

  if (!showShell) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <AppRoutes />
      </Box>
    );
  }

  return (
    <>
      <WorkspaceShell renderSidebar={renderSidebar} topbar={topbar} dock={dock}>
        <AppRoutes />
      </WorkspaceShell>
      <WorkspaceOverlays
        isDarkMode={isDarkMode}
        toggleTheme={toggleTheme}
        feedbackOpen={feedbackOpen}
        setFeedbackOpen={setFeedbackOpen}
      />
    </>
  );
}

/**
 * Router root: per-window providers that need the router (chat memory, editor bridge,
 * workspace layout).
 * @returns {JSX.Element} Shell.
 */
function AppShell() {
  return (
    <ChatMemoryProvider>
      <ActiveEditorProvider>
        <WorkspaceLayoutProvider>
          <AppContent />
        </WorkspaceLayoutProvider>
      </ActiveEditorProvider>
    </ChatMemoryProvider>
  );
}

// Data router (createBrowserRouter) is required for useBlocker-based
// unsaved-changes guards; route matching still happens in AppRoutes' <Routes>.
const router = createBrowserRouter([{ path: "*", element: <AppShell /> }]);

/**
 * Root application component.
 * @returns {JSX.Element} App root.
 */
function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => readStoredThemeMode() === "dark");
  const theme = isDarkMode ? darkTheme : lightTheme;

  // Keep <html data-theme> and the theme-color meta in sync after toggles (index.css and
  // non-MUI surfaces such as ProseMirror content follow data-theme).
  useEffect(() => {
    applyDocumentThemeMode(isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  const toggleTheme = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next ? "dark" : "light");
      } catch (error) {
        // Persistence is best-effort; the toggle still works for this session.
      }
      return next;
    });
  }, []);

  const themeMode = useMemo(() => ({ isDarkMode, toggleTheme }), [isDarkMode, toggleTheme]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SettingsProvider>
          <UsageProvider>
            <AppProvider>
              <AIRequestProvider>
                <AIJobCompletionReloader />
                <ThemeModeContext.Provider value={themeMode}>
                  <RouterProvider router={router} />
                </ThemeModeContext.Provider>
              </AIRequestProvider>
            </AppProvider>
          </UsageProvider>
        </SettingsProvider>
      </AuthProvider>
      {/* beforeSend strips the bearer token from /unsubscribe?t=... before any beacon leaves. */}
      <Analytics beforeSend={scrubUnsubscribeToken} />
      <SpeedInsights beforeSend={scrubUnsubscribeToken} />
    </ThemeProvider>
  );
}

export default App;
